import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as R from 'ramda';
import { convertDataCompanyId } from 'utils/helpers/format';
import { useUserRolePermissions } from 'hooks';
import { AUTH_TYPE as authorizationTypes } from 'feature/loading-management/utils/const';

const defaultToEmpty = R.defaultTo('');

const volumeValue = R.ifElse(
  R.isNil, //
  R.always('--'),
  value => `${value} gal.`
);

const CustomerItem = ({ label, value, variant, sx }) => (
  <Stack spacing={0.5} sx={sx}>
    <Typography variant="caption">{label}</Typography>
    <Typography variant={variant}>{value}</Typography>
  </Stack>
);
CustomerItem.propTypes = {
  label: PropTypes.string.isRequired,
  sx: PropTypes.shape({}),
  value: PropTypes.oneOfType([
    PropTypes.element, //
    PropTypes.number,
    PropTypes.string,
  ]),
  variant: PropTypes.string,
};
CustomerItem.defaultProps = {
  sx: null,
  value: null,
  variant: 'subtitle2',
};

const CustomerInformationAccordion = ({ customerAuthorization, defaultExpanded }) => {
  const {
    address,
    address2,
    authorizationStatus,
    authorizationType,
    authorizationVolume,
    city,
    customerClass,
    customerClassName,
    controlType,
    customerIdName,
    customerName,
    renewalVolume,
    state,
    zipCode,
  } = customerAuthorization;
  const [isExpanded, setExpanded] = useState(defaultExpanded);

  const { currentAffiliate: { customerNameShort, dataCompanyId } = {} } = useUserRolePermissions();

  const addedSpecific = R.concat(authorizationTypes, [
    {
      label: 'Specific',
      value: 'SPECIFIC',
    },
    {
      label: '1 Vol Daily',
      value: 'ONEVOLDAILY',
    },
    {
      label: '1 Vol Weekly',
      value: 'ONEVOLWEEKLY',
    },
    {
      label: '1 Vol Monthly',
      value: 'ONEVOLMONTH',
    },
  ]);

  const authorizationTypeLabel = R.compose(
    R.prop('label'),
    R.find(R.propEq('value', authorizationType))
  )(addedSpecific);

  const fullAddressLabel = `
    ${defaultToEmpty(address)}${address ? ',' : ' '} 
    ${defaultToEmpty(address2)}${address2 ? ',' : ' '}
    ${defaultToEmpty(city)}${city ? ',' : ' '}
    ${defaultToEmpty(state)} ${defaultToEmpty(zipCode)}`;

  const controlTypeLabel = R.cond([
    [R.equals('SINGLE'), R.always('Single')],
    [R.equals('GLOBAL'), R.always('Multiple Shared')],
    [R.equals('ALL'), R.always('Multiple')],
  ])(controlType);

  const affiliateLabel = `${customerNameShort} (${convertDataCompanyId(dataCompanyId)})`;

  const customerClassNameLabel = customerClass?.customerClassName ?? customerClassName;

  const chipStyles = theme => ({
    ...theme.typography.caption,
    '.MuiChip-label': {
      px: 1,
    },
    backgroundColor: authorizationStatus === 'Active' ? 'success.light' : 'graphic.light',
    height: 20,
    lineHeight: '16px',
    width: authorizationStatus === 'Active' ? 54 : 62,
  });

  return (
    <Accordion expanded={isExpanded} onChange={() => setExpanded(!isExpanded)} sx={{ m: 0 }}>
      <AccordionSummary
        aria-controls="customer-info-content"
        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
      >
        <Typography variant="body2">Customer Information</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2 }}>
          <CustomerItem
            label="Customer Name"
            sx={{ gridColumn: '1/3' }}
            value={customerName}
            variant="h5"
          />
          <CustomerItem label="Customer ID" value={customerIdName} />
          {authorizationStatus && (
            <CustomerItem
              label="Authorization Status"
              value={<Chip label={authorizationStatus} sx={chipStyles} />}
            />
          )}
          <CustomerItem label="Customer Address" value={fullAddressLabel} />
          <CustomerItem label="Control Type" value={controlTypeLabel} />
          <CustomerItem label="Affiliate" value={affiliateLabel} />
          <CustomerItem label="Authorization Type" value={authorizationTypeLabel} />
          <CustomerItem label="Authorization Volume" value={volumeValue(authorizationVolume)} />
          <CustomerItem label="Renewal Volume" value={volumeValue(renewalVolume)} />
          {customerClassNameLabel && <CustomerItem label="Class" value={customerClassNameLabel} />}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

CustomerInformationAccordion.propTypes = {
  customerAuthorization: PropTypes.shape({
    address: PropTypes.string,
    address2: PropTypes.string,
    authorizationStatus: PropTypes.string,
    authorizationType: PropTypes.string,
    authorizationVolume: PropTypes.number,
    city: PropTypes.string,
    controlType: PropTypes.string,
    customerClass: PropTypes.shape({
      customerClassName: PropTypes.string,
    }),
    customerClassName: PropTypes.string,
    customerIdName: PropTypes.string,
    customerName: PropTypes.string,
    renewalVolume: PropTypes.number,
    state: PropTypes.string,
    zipCode: PropTypes.string,
  }).isRequired,
  defaultExpanded: PropTypes.bool,
};

CustomerInformationAccordion.defaultProps = {
  defaultExpanded: false,
};

export default CustomerInformationAccordion;
